import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from '@lib/i18n/useTranslation'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { InvitationAcceptanceDialog } from '@/features/auth/components/InvitationAcceptanceDialog'
import { InviteRegistrationForm, listedCoApplicantCount } from '@/features/auth/components/InviteRegistrationForm'
import { InviteOnboardingLayout } from '@/features/auth/components/InviteOnboardingStepper'
import { saveInvitationAcceptance } from '@/lib/api/inviteService'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  registerUser,
  selectAuthLoading,
  selectCurrentInviteId,
  selectInviteData,
  clearError,
  setInviteData,
  setCurrentInviteId,
} from '@/features/auth/authSlice'
import { fetchApplications } from '@/features/applicant/applicationSlice'
import type { InviteData } from '@/types/auth'
import { formatDateForInput, unformatPhoneNumber, formatPhoneNumber } from '@/types/application'
import { buildInvitePropertyPayload } from '@/features/auth/utils/inviteRegistrationProperty'
import { getStorageItem, getStorageJSON, STORAGE_KEYS } from '@/lib/utils/storage'
import { getInvitationData } from '@/lib/api/inviteService'
import {
  getCoApplicantInviteDetails,
  getInviteDetails,
} from '@/features/apply/services/applyService'
import { validateDateOfBirth } from '@/lib/utils/dateValidation'
import { showSuccess } from '@/lib/utils/notifications'
import type { ApplyErrorDialog, VerifyInfoFormData, VerifyInfoSetPasswordProps } from '../types'

/**
 * Validation schema for form
 */
/**
 * Angular `signup.fromgroup.ts:9-17` — no leading/trailing space, at most three
 * space-separated tokens, restricted punctuation set.
 */
const NAME_PATTERN =
  /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+){0,2}(?<! )$/

const verifyInfoSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'First name is required')
      .regex(NAME_PATTERN, 'Enter Valid First Name.'),
    middleName: z
      .string()
      .refine((val) => !val || NAME_PATTERN.test(val), 'Enter Valid Middle Name.'),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .regex(NAME_PATTERN, 'Enter Valid Last Name.'),
    dob: z.string().superRefine((value, ctx) => {
      const result = validateDateOfBirth(value)
      if (!result.isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result.error || 'Date of Birth is required',
        })
      }
    }),
    ssn: z.string().superRefine((value, ctx) => {
      const digits = value.replace(/\D/g, '')
      if (!digits) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'SSN or ITIN is required' })
        return
      }
      if (digits.length !== 9) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'SSN or ITIN must be 9 digits' })
      }
    }),
    communicationPreference: z.string().refine((value) => value === 'Text Msg' || value === 'Email', {
      message: 'Communication Preference is required',
    }),
    expectedResidents: z.string().superRefine((value, ctx) => {
      const trimmed = value.trim()
      if (!trimmed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Expected Resident(s) required',
        })
        return
      }
      if (!/^[1-9]$/.test(trimmed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Expected Resident(s) must be a whole number from 1 to 9.',
        })
      }
    }),
    email: z
      .string()
      .min(1, 'Email address is required')
      .email('Please enter a valid email address'),
    mobilePhone: z
      .string()
      .min(1, 'Phone number is required')
      // Angular mobileValidator: /^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/ on
      // digits only — an optional 1-2 digit country code plus 10 digits.
      .refine((val) => {
        const digits = val.replace(/\D/g, '')
        return digits.length >= 10 && digits.length <= 12
      }, 'Enter Valid Phone Number. Should match pattern [+X (XXX) XXX-XXXX].'),
    isSharedPhone: z.boolean(),
    password: z
      .string()
      .min(1, 'Password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

/**
 * Angular: `location.href.replace(this.inviteId, response.new_invite_code)` — swaps the
 * code in place so `/co-applicant/apply/…` keeps its prefix (register.component.ts:161).
 */
function redirectToNewInviteCode(currentCode: string, newCode: string): void {
  window.location.href = window.location.href.replace(currentCode, newCode)
}

/**
 * Map invitation data to form data
 */
function defaultExpectedResidents(invitationData: InviteData): string {
  const count = listedCoApplicantCount(invitationData.co_applicant)
  if (count < 1) return ''
  return String(Math.min(count, 9))
}

function mapInvitationDataToForm(invitationData: InviteData): Partial<VerifyInfoFormData> {
  // Format phone number if it exists
  const formattedPhone = invitationData.mobile_no 
    ? formatPhoneNumber(invitationData.mobile_no) 
    : ''
  
  return {
    firstName: invitationData.first_name || '',
    middleName: invitationData.middle_name || '',
    lastName: invitationData.last_name || '',
    dob: invitationData.birthdate ? formatDateForInput(invitationData.birthdate) : '',
    ssn: '',
    communicationPreference: 'Email',
    expectedResidents: defaultExpectedResidents(invitationData),
    email: invitationData.email || '',
    mobilePhone: formattedPhone,
    isSharedPhone: false,
  }
}

export function VerifyInfoSetPassword({
  isCoApplicant = false,
}: VerifyInfoSetPasswordProps = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  // `/apply/:inviteId` (Apply Now) — keep path; reuse this UI without numbered steps
  const { inviteId: routeInviteId } = useParams<{ inviteId?: string }>()
  
  const [errorDialog, setErrorDialog] = useState<ApplyErrorDialog | null>(null)
  // URL-supplied code resolved (or typed via the in-place wizard).
  const [inviteResolved, setInviteResolved] = useState(false)
  const [acceptanceSaving, setAcceptanceSaving] = useState(false)
  const [acceptanceError, setAcceptanceError] = useState('')
  const [acceptanceDone, setAcceptanceDone] = useState(false)
  const [inviteGateMessage, setInviteGateMessage] = useState('')
  const inviteDetailsFetchedRef = useRef(false)
  const autofillScrubbedRef = useRef(false)
  // Must be declared unconditionally (before any early return) to keep hook order stable.
  const acceptanceInviteRefreshed = useRef(false)
  const registerIntroRefreshedFor = useRef<string | null>(null)

  // Redux state
  const isLoading = useAppSelector(selectAuthLoading)
  const reduxInviteData = useAppSelector(selectInviteData)
  const reduxInviteCode = useAppSelector(selectCurrentInviteId)

  // Load invitation data from localStorage or Redux
  const invitationData = useMemo(() => {
    // First check Redux state
    if (reduxInviteData) {
      return reduxInviteData
    }
    
    // Fall back to localStorage
    return getStorageJSON<InviteData>(STORAGE_KEYS.invitationData)
  }, [reduxInviteData])

  // Prefer invite code from `/apply/:inviteId` URL, then Redux / storage
  const inviteCode = useMemo(() => {
    return (
      routeInviteId?.trim() ||
      reduxInviteCode ||
      getStorageItem(STORAGE_KEYS.inviteCode) ||
      ''
    )
  }, [routeInviteId, reduxInviteCode])


  // Get default values from invitation data
  const defaultValues = useMemo(() => {
    const baseDefaults: Partial<VerifyInfoFormData> = {
      isSharedPhone: false,
      firstName: '',
      middleName: '',
      lastName: '',
      dob: '',
      ssn: '',
      communicationPreference: 'Email',
      expectedResidents: '',
      email: '',
      mobilePhone: '',
      password: '',
      confirmPassword: '',
    }

    if (invitationData) {
      const mappedData = mapInvitationDataToForm(invitationData)
      return { ...baseDefaults, ...mappedData }
    }

    return baseDefaults
  }, [invitationData])

  // Track which fields are autofilled from invitation data
  const autoFilledFields = useMemo(() => {
    const fields = new Set<string>()
    
    if (invitationData) {
      if (invitationData.first_name) fields.add('firstName')
      if (invitationData.last_name) fields.add('lastName')
      if (invitationData.email) {
        fields.add('email')
      }
      if (invitationData.mobile_no) fields.add('mobilePhone')
    }
    
    return fields
  }, [invitationData])

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<VerifyInfoFormData>({
    resolver: zodResolver(verifyInfoSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: defaultValues as VerifyInfoFormData,
  })

  // Reset form when invitation data is loaded (Angular: signupForm.patchValue(response))
  useEffect(() => {
    if (!invitationData) return
    if (!invitationData.first_name && !invitationData.email && !invitationData.mobile_no) {
      return
    }
    reset({
      isSharedPhone: false,
      firstName: invitationData.first_name || '',
      middleName: invitationData.middle_name || '',
      lastName: invitationData.last_name || '',
      dob: invitationData.birthdate
        ? formatDateForInput(invitationData.birthdate)
        : '',
      ssn: getValues('ssn') || '',
      communicationPreference: getValues('communicationPreference') || 'Email',
      expectedResidents: getValues('expectedResidents') || defaultExpectedResidents(invitationData),
      email: invitationData.email || '',
      mobilePhone: invitationData.mobile_no
        ? formatPhoneNumber(invitationData.mobile_no)
        : '',
      password: '',
      confirmPassword: '',
    })
  }, [invitationData, reset])

  // Browser often autofills email into DOB and saved password into Password — clear once on load
  useEffect(() => {
    if (autofillScrubbedRef.current) return
    const timer = window.setTimeout(() => {
      autofillScrubbedRef.current = true
      const dobValue = getValues('dob') || ''
      // Email wrongly injected into DOB (contains @)
      if (dobValue.includes('@')) {
        setValue('dob', '', { shouldValidate: false, shouldDirty: false })
      }
      // Clear browser-saved password autofill (user must set a new password)
      setValue('password', '', { shouldValidate: false, shouldDirty: false })
      setValue('confirmPassword', '', { shouldValidate: false, shouldDirty: false })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [getValues, setValue, invitationData])

  // Clear errors on mount
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const onSubmit = async (data: VerifyInfoFormData) => {
    setErrorDialog(null)
    dispatch(clearError())

    // Check if invite code exists
    if (!inviteCode) {
      setErrorDialog({
        title: 'Validation Message',
        message: 'Invite code is missing. Please go back and enter your invite code.',
        primaryLabel: 'Ok',
        onPrimary: () => setErrorDialog(null),
      })
      return
    }

    try {
      // Format data for API
      const registrationData = {
        email: data.email.trim().toLowerCase(),
        first_name: data.firstName.trim(),
        middle_name: data.middleName?.trim() || '',
        last_name: data.lastName.trim(),
        mobile_no: unformatPhoneNumber(data.mobilePhone),
        shared_phone_no: data.isSharedPhone ? '1' : '0',
        password: data.password,
        confirm_password: data.confirmPassword,
        invite_code: inviteCode,
        // Angular sends YYYY-MM-DD (payment.datahandler `formatDate`); DatePicker
        // already holds that format, so pass it through rather than reformatting.
        birthdate: data.dob.trim(),
        security_number: data.ssn.trim(),
        communication_preference: data.communicationPreference,
        expected_residents: Number(data.expectedResidents),
        other_individuals: Number(data.expectedResidents) > 1 ? '1' : '0',
        property: buildInvitePropertyPayload(
          invitationData,
          inviteCode,
          Number(data.expectedResidents),
        ),
        // Angular register.component.ts:312 — marks the registration as web-originated.
        is_web: true,
      }

      // Call register and auto-login
      const result = await dispatch(registerUser(registrationData)).unwrap()

      if (result) {
        if (result.token) {
          try {
            await dispatch(fetchApplications()).unwrap()
          } catch {
            // Continue to the application even if fetch fails
          }
        }
        showSuccess('Registration successful!')
        navigate('/applicant/application/invite')
      }
    } catch (error) {
      // Error from Redux thunk rejection — Angular notify(msg, 0) = "Validation Message" popup
      const errorMessage = typeof error === 'string' ? error : 'Registration failed. Please try again.'
      setErrorDialog({
        title: 'Validation Message',
        message: errorMessage,
        primaryLabel: 'Ok',
        onPrimary: () => setErrorDialog(null),
      })
    }
  }

  /**
   * Angular register.component.ts `getInvitationData` error branches:
   * `error.email` → Sign In prefilled; invite-used / already-registered → Sign In; otherwise notify().
   */
  const openInviteErrorDialog = useCallback((err: unknown) => {
    const detail = (err ?? {}) as {
      message?: string
      email?: string
      type?: string
      newInviteCode?: string
    }
    const message = detail.message || 'Failed to load invite details'
    setInviteGateMessage(message)
    const goToLogin = (email?: string) => {
      navigate(email ? `/login?email=${encodeURIComponent(email)}` : '/login')
    }

    // Expired code — Angular replaces the code in the URL and reloads the page.
    if (detail.newInviteCode) {
      setErrorDialog({
        title: 'Invite Code Expired',
        message: `We found New matching Invite code ${detail.newInviteCode}, Use this invite code`,
        primaryLabel: 'Yes, Use This',
        onPrimary: () => {
          redirectToNewInviteCode(routeInviteId ?? '', detail.newInviteCode as string)
        },
      })
      return
    }

    if (detail.email) {
      setErrorDialog({
        title: 'Validation Message',
        message,
        primaryLabel: 'Ok',
        onPrimary: () => goToLogin(detail.email),
        onDismiss: () => goToLogin(detail.email),
      })
      return
    }
    if (detail.type === 'INVITE_ALREADY_USED') {
      setErrorDialog({
        title: 'Invitation Error',
        message,
        primaryLabel: 'Ok',
        onPrimary: () => goToLogin(),
        onDismiss: () => goToLogin(),
      })
      return
    }
    if (detail.type === 'USER_ALREADY_REGISTERED') {
      setErrorDialog({
        title: 'Validation Message',
        message,
        primaryLabel: 'Ok',
        onPrimary: () => goToLogin(),
        onDismiss: () => goToLogin(),
      })
      return
    }
    setErrorDialog({
      title: 'Validation Message',
      message,
      primaryLabel: 'Ok',
      onPrimary: () => goToLogin(),
      onDismiss: () => goToLogin(),
    })
  }, [navigate, routeInviteId])

  // Redirect if no invite data (only for apply-now wizard — not `/apply/:inviteId`)
  useEffect(() => {
    if (routeInviteId) return
    if (!invitationData && !inviteCode) {
      const timeout = setTimeout(() => {
        if (!invitationData && !inviteCode) {
          navigate('/apply-now/enter-invite-code')
        }
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [invitationData, inviteCode, navigate, routeInviteId])

  // Load invite from URL — Angular getInvitationData parity (GET /invite/:id?page=register)
  // Important: do NOT cancel/skip when Redux invite id updates (that was clearing prefill).
  useEffect(() => {
    const codeFromUrl = routeInviteId?.trim()
    if (!codeFromUrl) {
      return
    }

    let cancelled = false
    inviteDetailsFetchedRef.current = true
    dispatch(setCurrentInviteId(codeFromUrl))

    ;(async () => {
      setErrorDialog(null)
      setInviteGateMessage('')
      try {
        // Same public endpoint Angular uses for /apply/:inviteId
        // Angular: `params.coapplicant_inviteId` → `/coapplicant_invite/:id`, and
        // `onInviteSubmit` also branches on a `CO-` prefix (register.component.ts:147).
        const useCoApplicant = isCoApplicant || codeFromUrl.toUpperCase().startsWith('CO-')
        const details = useCoApplicant
          ? await getCoApplicantInviteDetails(codeFromUrl)
          : await getInviteDetails(codeFromUrl)
        if (cancelled) return

        if (details.new_invite_code) {
          redirectToNewInviteCode(codeFromUrl, details.new_invite_code)
          return
        }

        dispatch(setInviteData(details as InviteData))
        setInviteResolved(true)
      } catch (err: unknown) {
        if (cancelled) return
        // Terminal errors (already registered / invite used) — popup now, no retry.
        const detail = (err ?? {}) as { email?: string; type?: string; message?: string }
        const message = detail.message || ''
        if (
          detail.email ||
          detail.type ||
          /please sign in/i.test(message) ||
          /already exits/i.test(message) ||
          /already exists/i.test(message)
        ) {
          openInviteErrorDialog(err)
          return
        }
        // Fallback to shared invite lookup (also tries co-applicant endpoint)
        try {
          const response = await getInvitationData(codeFromUrl)
          if (cancelled) return
          if (response.success && response.data) {
            const data = response.data as InviteData & { new_invite_code?: string }
            if (data.new_invite_code) {
              redirectToNewInviteCode(codeFromUrl, data.new_invite_code)
              return
            }
            dispatch(setInviteData(data))
            setInviteResolved(true)
            return
          }
        } catch {
          /* use primary error below */
        }

        openInviteErrorDialog(err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [routeInviteId, dispatch, openInviteErrorDialog, isCoApplicant])

  // apply-now path: hydrate from storage invite code when Redux has no details yet
  useEffect(() => {
    if (routeInviteId) return
    if (!inviteCode || invitationData?.first_name) return
    if (inviteDetailsFetchedRef.current) return

    inviteDetailsFetchedRef.current = true
    let cancelled = false
    ;(async () => {
      try {
        const response = await getInvitationData(inviteCode)
        if (!cancelled && response.success && response.data) {
          dispatch(setInviteData(response.data as InviteData))
        }
      } catch {
        /* keep empty form */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [routeInviteId, inviteCode, invitationData, dispatch])

  const acceptanceRequired =
    Boolean(invitationData) &&
    String(invitationData?.invitation_acceptance_completed ?? '') !== '1' &&
    !acceptanceDone

  const showRegistrationForm =
    inviteResolved && Boolean(invitationData) && !acceptanceRequired

  // Fresh org invite HTML (invite_*_html) — avoid stale localStorage / pre-migration defaults.
  useEffect(() => {
    if (!acceptanceRequired) {
      acceptanceInviteRefreshed.current = false
      return
    }
    const code = inviteCode?.trim()
    if (!code || acceptanceInviteRefreshed.current) return

    acceptanceInviteRefreshed.current = true
    let cancelled = false
    ;(async () => {
      try {
        const useCoApplicant =
          isCoApplicant || code.toUpperCase().startsWith('CO-') || code.length === 13
        const details = useCoApplicant
          ? await getCoApplicantInviteDetails(code)
          : await getInviteDetails(code)
        if (cancelled) return
        dispatch(
          setInviteData({
            ...(invitationData ?? {}),
            ...details,
          } as InviteData),
        )
      } catch {
        /* keep existing invitationData */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [acceptanceRequired, inviteCode, isCoApplicant, dispatch, invitationData])

  // Fresh invite_intro_html (and related org copy) when registration form is shown — parity with mobile refetch.
  useEffect(() => {
    if (!showRegistrationForm) return
    const code = inviteCode?.trim()
    if (!code || registerIntroRefreshedFor.current === code) return

    registerIntroRefreshedFor.current = code
    let cancelled = false
    ;(async () => {
      try {
        const useCoApplicant =
          isCoApplicant || code.toUpperCase().startsWith('CO-') || code.length === 13
        const details = useCoApplicant
          ? await getCoApplicantInviteDetails(code)
          : await getInviteDetails(code)
        if (cancelled) return
        dispatch(
          setInviteData({
            ...(invitationData ?? {}),
            ...details,
            invitation_acceptance_completed:
              invitationData?.invitation_acceptance_completed === '1' ||
              acceptanceDone
                ? '1'
                : details.invitation_acceptance_completed,
          } as InviteData),
        )
      } catch {
        /* keep existing invitationData */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    showRegistrationForm,
    inviteCode,
    isCoApplicant,
    dispatch,
    invitationData,
    acceptanceDone,
  ])

  const completeAcceptance = async (payload: {
    fees: Record<string, boolean>
    eligibility: Record<string, boolean>
  }) => {
    if (!inviteCode) return
    setAcceptanceSaving(true)
    setAcceptanceError('')
    try {
      const coApplicantCode = isCoApplicant || inviteCode.toUpperCase().startsWith('CO-') || inviteCode.length === 13
      if (!coApplicantCode) {
        await saveInvitationAcceptance(inviteCode, payload)
      }
      setAcceptanceDone(true)
      if (invitationData) {
        dispatch(setInviteData({ ...invitationData, invitation_acceptance_completed: '1' }))
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Please acknowledge all items.'
      setAcceptanceError(message)
    } finally {
      setAcceptanceSaving(false)
    }
  }

  const renderErrorDialog = () =>
    errorDialog ? (
      <OnScreenModal
        isOpen
        title={errorDialog.title}
        maxWidth="max-w-md"
        onClose={() =>
          errorDialog.onDismiss ? errorDialog.onDismiss() : setErrorDialog(null)
        }
        panelDataTestId="apply-error-dialog"
        footer={
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={errorDialog.onPrimary}
              className="min-w-[9rem] rounded-md bg-teal-dark px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-darker"
              data-testid="apply-error-dialog-primary"
            >
              {errorDialog.primaryLabel}
            </button>
          </div>
        }
      >
        <div className="px-1 py-5 text-center">
          <p className="text-sm leading-relaxed text-slate-700">
            {errorDialog.message}
          </p>
        </div>
      </OnScreenModal>
    ) : null

  /**
   * `/apply/:inviteId` opens on the invitation welcome step. The invite GET
   * (already registered, invite not found, expired code) stays on this page.
   */
  if (routeInviteId && !inviteResolved) {
    return (
      <>
        {inviteGateMessage ? (
          <div className="min-h-screen bg-[#f4f7fb]" data-testid="invite-gate-backdrop" />
        ) : (
          <InvitationAcceptanceDialog
            welcomeHtml=""
            feesHtml=""
            eligibilityHtml=""
            saving={false}
            loading
            startDisabled
            onComplete={() => {}}
          />
        )}
        {renderErrorDialog()}
      </>
    )
  }

  if (acceptanceRequired) {
    return (
      <>
        <InvitationAcceptanceDialog
          welcomeHtml={invitationData?.invite_welcome_html || ''}
          feesHtml={invitationData?.invite_fees_html || ''}
          eligibilityHtml={invitationData?.invite_eligibility_html || ''}
          saving={acceptanceSaving}
          error={acceptanceError}
          onComplete={completeAcceptance}
        />
        {renderErrorDialog()}
      </>
    )
  }

  return (
    <>
      <InviteOnboardingLayout
        currentStep="registration"
        title="Complete your invitation."
      >
        <InviteRegistrationForm
          invitationData={invitationData}
          inviteCode={inviteCode || ''}
          autoFilledFields={autoFilledFields}
          control={control}
          register={register}
          errors={errors}
          setValue={setValue}
          trigger={trigger}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit(onSubmit)}
          t={t}
          embedded
        />
      </InviteOnboardingLayout>
      {renderErrorDialog()}
    </>
  )
}

